import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { siteImages } from '../data/mock';
import { Target, Users, Award, Heart, Sparkles, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  useEffect(() => { document.title = 'About Us | Desk Line Supply'; }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />

      <main>
        {/* Banner */}
        <section className="relative h-64 md:h-80 overflow-hidden bg-dark-900">
          <img src={siteImages.aboutBanner} alt="About Us" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-dark-900 via-dark-900/85 to-dark-900/40" />
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/15 rounded-full text-athletic-300 text-xs font-semibold mb-4">
                <Sparkles className="w-3.5 h-3.5" /> OUR STORY
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-white font-bold mb-3">About Desk Line Supply</h1>
              <p className="text-dark-200 max-w-lg">A curated home for office supplies and workspace essentials worth owning.</p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Mission */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <span className="inline-block px-4 py-1.5 bg-athletic-50 text-athletic-700 text-xs font-bold uppercase tracking-wider rounded-full mb-4">Our Mission</span>
              <h2 className="font-display text-3xl lg:text-4xl text-dark-900 font-bold mb-6">Helping you build a workspace that just <span className="text-athletic-600">works</span></h2>
              <p className="text-dark-600 text-lg leading-relaxed mb-6">
                Desk Line Supply started with a simple frustration: outfitting a workspace should feel effortless, not exhausting. Instead of endless lookalike listings, we hand-pick a compact lineup of supplies &mdash; from desk organization and writing tools to electronics, furniture and decor &mdash; that we would happily put on our own desks.
              </p>
              <p className="text-dark-600 leading-relaxed mb-8">
                Every product is chosen for real-world durability, clear value and everyday reliability. Whether you are setting up a first home office, refreshing a studio or equipping a growing team, we make it simple to buy with confidence and get it shipped fast.
              </p>
              <div className="flex flex-wrap gap-4">
                {['Curated Selection', 'Fair Pricing', 'Fast Free Shipping'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 px-4 py-2 bg-dark-50 rounded-xl border border-dark-100">
                    <CheckCircle2 className="w-4 h-4 text-athletic-600" />
                    <span className="text-dark-900 text-sm font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-3xl overflow-hidden border border-dark-100">
                <img src={siteImages.heroLifestyle} alt="Desk Line Supply organized workspace" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-athletic-500 text-white px-6 py-4 rounded-2xl shadow-elevated">
                <p className="font-display font-bold text-2xl">14+</p>
                <p className="text-sm font-semibold">Handpicked Essentials</p>
              </div>
            </div>
          </div>

          {/* Core Values */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-athletic-50 text-athletic-700 text-xs font-bold uppercase tracking-wider rounded-full mb-4">Core Values</span>
              <h2 className="font-display text-3xl lg:text-4xl text-dark-900 font-bold">What drives us</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Target, title: 'Quality First', desc: 'Each item is chosen for performance, build and everyday value' },
                { icon: Users, title: 'Made for You', desc: 'We shop the way you do — for supplies that fit real workdays' },
                { icon: Award, title: 'Excellence', desc: 'We only stock products we would confidently recommend to a friend' },
                { icon: Heart, title: 'Passion', desc: 'We genuinely love a well-stocked desk and the focus it unlocks' },
              ].map((value, i) => (
                <div key={i} className="bg-dark-50 border border-dark-100 rounded-2xl p-6 hover:border-athletic-300 transition-all group">
                  <div className="w-14 h-14 bg-athletic-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-athletic-100 transition-colors">
                    <value.icon className="w-7 h-7 text-athletic-700" />
                  </div>
                  <h3 className="font-display font-bold text-dark-900 text-lg mb-2">{value.title}</h3>
                  <p className="text-dark-500 text-sm leading-relaxed">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Why choose */}
          <div className="bg-dark-50 border border-dark-100 rounded-3xl p-8 md:p-12">
            <div className="text-center mb-10">
              <h2 className="font-display text-3xl lg:text-4xl text-dark-900 font-bold mb-4">Why shop with Desk Line Supply?</h2>
              <p className="text-dark-500 max-w-2xl mx-auto">We sweat the details so every order feels easy &mdash; from browse to unboxing.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                'A tight, curated office-supply lineup',
                'Free USPS shipping across the USA',
                'Secure checkout with SSL encryption',
                '30-day hassle-free returns',
                'Friendly, responsive support',
                'Honest product descriptions',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-dark-100">
                  <CheckCircle2 className="w-5 h-5 text-athletic-600 flex-shrink-0" />
                  <span className="text-dark-900 font-medium">{item}</span>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/shop" className="inline-flex items-center gap-2 px-8 py-4 bg-athletic-500 text-white font-bold uppercase tracking-wider rounded-lg hover:opacity-90 transition-all shadow-button">Shop Our Products</Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
